import {addDays} from 'date-fns'

import {businessCalendarDate} from 'src/api/business-time'
import {DISPATCH_TYPEAHEAD_ASSIGNMENT_STRIP_DATE_RANGE} from 'src/assignments/assignment-strip-utils'

import {formatJobTime, toApiDateString} from '../jobs/job-schedule-format'
import {boardJobScheduleDateRange, referenceDateForBoardJob} from './dispatch-board-resource-strip'
import type {
    BookingDays,
    DispatchAssignment,
    DispatchJobSchedule,
    DispatchResource,
    DispatchTimelineData,
    DriverFitGroup,
    DriverFitOption,
    UnassignedJob,
} from './types'

type FitContext = {
    job: UnassignedJob
    resources: DispatchResource[]
    data: DispatchTimelineData
    bookingDays?: BookingDays
}

type DayRange = {from: string; until: string | null}

function bookedDayRange(
    schedule: Pick<DispatchJobSchedule, 'startDate' | 'endDate'> | null,
    days?: BookingDays,
): DayRange | null {
    if (!schedule) {
        return null
    }
    if (!days) {
        return {from: schedule.startDate, until: schedule.endDate}
    }

    const from = days.from > schedule.startDate ? days.from : schedule.startDate
    const until =
        days.until == null
            ? schedule.endDate
            : schedule.endDate == null || days.until < schedule.endDate
              ? days.until
              : schedule.endDate
    return until != null && until < from ? null : {from, until}
}

function dayRangesOverlap(left: DayRange, right: DayRange): boolean {
    return left.from <= (right.until ?? '9999-12-31') && right.from <= (left.until ?? '9999-12-31')
}

function jobScheduleDays(
    job: UnassignedJob | DispatchAssignment,
    data?: DispatchTimelineData,
): Pick<DispatchJobSchedule, 'startDate' | 'endDate'> | null {
    return job.schedule ?? boardJobScheduleDateRange(job as UnassignedJob, data)
}

type ScoredDriver = DriverFitOption & {score: number}

export function isBorrowedDriver(resource: DispatchResource, job: UnassignedJob): boolean {
    return (
        job.dispatchYardId != null &&
        resource.homeYardId != null &&
        resource.homeYardId !== job.dispatchYardId
    )
}

export const BORROWED_DRIVER_REASON = 'Driver from another yard'

export function driverUnavailableFor(
    resource: DispatchResource,
    job: UnassignedJob,
    bookingDays?: BookingDays,
    today = toApiDateString(businessCalendarDate()),
): {label: string; detail: string} | undefined {
    const range = bookedDayRange(job.schedule ?? null, bookingDays)
    if (!resource.unavailable?.length || !range) {
        return undefined
    }

    const from = range.from < today ? today : range.from
    if (range.until != null && range.until < from) {
        return undefined
    }

    return resource.unavailable.find((entry) =>
        dayRangesOverlap({from, until: range.until}, {from: entry.from, until: entry.until}),
    )
}

export const ALSO_BOOKED_PREFIX = 'Also on '

export function sameDayBookingLabel(
    resourceId: string,
    job: UnassignedJob,
    data: DispatchTimelineData,
    bookingDays?: BookingDays,
    today = toApiDateString(businessCalendarDate()),
): string | undefined {
    const ownDays =
        bookingDays ??
        job.allocations.find(
            (allocation) => allocation.kind === 'worker' && allocation.resourceId === resourceId,
        )?.days
    const booked = bookedDayRange(jobScheduleDays(job, data), ownDays)
    if (!booked) {
        return undefined
    }

    const target = {from: booked.from < today ? today : booked.from, until: booked.until}
    if (target.until != null && target.until < target.from) {
        return undefined
    }

    for (const {job: other, range} of listOtherJobBookingsForResource(resourceId, job, data)) {
        if (dayRangesOverlap(target, range)) {
            const startTime = other.schedule?.startTime
            return `${ALSO_BOOKED_PREFIX}${other.label}${startTime ? ` (starts ${formatJobTime(startTime)})` : ''}`
        }
    }
    return undefined
}

function listOtherJobBookingsForResource(
    resourceId: string,
    job: UnassignedJob,
    data: DispatchTimelineData,
): Array<{job: UnassignedJob | DispatchAssignment; range: DayRange}> {
    // unassignedJobs holds every live board job; assignments holds committed jobs.
    const bookings = [
        ...data.unassignedJobs.map((other) => ({
            jobId: other.id,
            job: other,
            booking: other.allocations.find((allocation) => allocation.resourceId === resourceId),
        })),
        ...data.assignments.map((other) => ({
            jobId: other.sourceJobId ?? other.id,
            job: other,
            booking:
                other.allocations?.find((allocation) => allocation.resourceId === resourceId) ??
                (other.resourceId === resourceId ? {days: undefined} : undefined),
        })),
    ]
    const seen = new Set<string>([job.id])
    const found: Array<{job: UnassignedJob | DispatchAssignment; range: DayRange}> = []
    for (const {jobId, job: other, booking} of bookings) {
        if (!booking || seen.has(jobId)) {
            continue
        }
        seen.add(jobId)
        const range = bookedDayRange(jobScheduleDays(other, data), booking.days)
        if (range) {
            found.push({job: other, range})
        }
    }
    return found
}

function hasOpenWeekAroundJob(
    resourceId: string,
    job: UnassignedJob,
    data: DispatchTimelineData,
): boolean {
    const jobDay = referenceDateForBoardJob(job, data)
    const {daysBefore = 0, daysAfter = 0} = DISPATCH_TYPEAHEAD_ASSIGNMENT_STRIP_DATE_RANGE
    const week = {
        from: toApiDateString(addDays(jobDay, -daysBefore)),
        until: toApiDateString(addDays(jobDay, daysAfter)),
    }
    return !listOtherJobBookingsForResource(resourceId, job, data).some(({range}) =>
        dayRangesOverlap(week, range),
    )
}

export function buildDriverFitOptions(context: FitContext): DriverFitOption[] {
    const {job, resources, data, bookingDays} = context
    const scored: ScoredDriver[] = []

    for (const resource of resources) {
        if (resource.outOfService || driverUnavailableFor(resource, job, bookingDays)) {
            continue
        }

        const reasons: string[] = []
        let score = 50

        const sameDay = sameDayBookingLabel(resource.id, job, data, bookingDays)
        if (sameDay) {
            reasons.unshift(sameDay)
            score -= 15
        }

        if (hasOpenWeekAroundJob(resource.id, job, data)) {
            reasons.push('Open schedule')
            score += 10
        } else {
            score += 4
        }

        if (isBorrowedDriver(resource, job)) {
            reasons.unshift(BORROWED_DRIVER_REASON)
            score -= 30
        }

        const group: DriverFitGroup = score >= 75 ? 'recommended' : 'available'
        scored.push({
            driverId: resource.id,
            driverName: resource.name,
            group,
            reasons,
            score,
        })
    }

    // Own-yard drivers first; borrowed drivers follow, labelled with their home yard.
    const isBorrowed = (entry: ScoredDriver) => entry.reasons.includes(BORROWED_DRIVER_REASON)
    const ranked = [...scored].sort(
        (a, b) => Number(isBorrowed(a)) - Number(isBorrowed(b)) || b.score - a.score,
    )
    const recommended = ranked.slice(0, Math.min(3, ranked.length))
    const recommendedIds = new Set(recommended.map((entry) => entry.driverId))
    const available = ranked.filter((entry) => !recommendedIds.has(entry.driverId))

    return [
        ...recommended.map((entry) => ({...entry, group: 'recommended' as const})),
        ...available.map((entry) => ({...entry, group: 'available' as const})),
    ]
}

export function groupFitOptions(
    options: DriverFitOption[],
): Record<DriverFitGroup, DriverFitOption[]> {
    return {
        recommended: options.filter((option) => option.group === 'recommended'),
        available: options.filter((option) => option.group === 'available'),
    }
}
